import { App, Checkbox, DatePicker, Form, Input, Select } from "antd";
import day from "dayjs";
import isEmpty from "lodash/isEmpty";

import { Modal } from "components/modal";
import { fetcher } from "data/fetcher";
import { roundPerformedOn } from "./perform-activity/round-performed-on";

const { RangePicker } = DatePicker;

const toUnixSeconds = (d) => {
  if (!d) return null;
  if (typeof d.unix === "function") return d.unix();

  const t = d?.toDate?.().getTime?.();
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
};

const normalizeEmails = (emails = []) => {
  return (emails || [])
    .filter((e) => typeof e === "string")
    .map((e) => e.trim())
    .filter(Boolean);
};

export const CreateCalendarEventModal = (props) => {
  const {
    accountId,
    close,
    contactEmails = [],
    contactId,
    isOpen,
    onCreated,
    opportunityId,
    profileId,
    vendorId,
  } = props;
  const { message } = App.useApp();

  const [form] = Form.useForm();

  const initialStart = roundPerformedOn(day().add(1, "hour"));
  const initialRange = [initialStart, initialStart.add(1, "hour")];

  return (
    <Modal
      maskClosable={false}
      okText="Create"
      onCancel={close}
      onOk={form.submit}
      open={isOpen}
      title="Create Calendar Event"
    >
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        initialValues={{
          subject: "",
          when: initialRange,
          attendees: normalizeEmails(contactEmails),
          location: "",
          body: "",
          shouldFollowUp: true,
        }}
        onFinish={async (values) => {
          const when = values.when || [];
          const start = when?.[0];
          const end = when?.[1];

          if (!start || !end) {
            message.error("Please choose a start and end time.");
            return;
          }

          const attendees = normalizeEmails(values.attendees);

          const shouldFollowUp = Boolean(values.shouldFollowUp);
          const hasEntity =
            accountId || contactId || opportunityId || vendorId ? true : false;
          const hasAssignee = Boolean(profileId);

          const startUnix = toUnixSeconds(start);
          const endUnix = toUnixSeconds(end);

          const { event } = await fetcher.post(
            "/integrations/microsoft-graph/events"
          )({
            subject: values.subject,
            start: startUnix,
            end: endUnix,
            location: values.location,
            body: values.body,
            attendees,
          });

          if (shouldFollowUp && event?.id && hasEntity && hasAssignee) {
            const base = {
              assigneeId: profileId,
              dueOn: event?.end ? event.end : endUnix,
              task: "Followup on calendar event",
              integrations: [
                {
                  type: "microsoft_graph_event",
                  payload: {
                    eventId: event.id,
                    userId: profileId,
                  },
                },
              ],
            };

            if (accountId) {
              await fetcher.post("/followup/account")({
                ...base,
                accountId,
              });
            } else if (contactId) {
              await fetcher.post("/followup/contact")({
                ...base,
                contactId,
              });
            } else if (opportunityId) {
              await fetcher.post("/followup/opportunity")({
                ...base,
                opportunityId,
              });
            } else if (vendorId) {
              await fetcher.post("/followup/vendor")({
                ...base,
                vendorId,
              });
            }
          }

          if (onCreated) {
            await onCreated();
          }

          close();
        }}
      >
        <Form.Item label="Subject" name="subject" rules={[{ required: true }]}>
          <Input placeholder="Intro call" />
        </Form.Item>

        <Form.Item label="When" name="when" rules={[{ required: true }]}>
          <RangePicker
            format={"YYYY-MM-DD HH:mm"}
            showTime={{ format: "HH:mm", minuteStep: 15 }}
            allowClear={false}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item label="Attendees" name="attendees">
          <Select
            mode="tags"
            placeholder="email@company.com"
            tokenSeparators={[",", " "]}
            options={normalizeEmails(contactEmails).map((e) => ({
              label: e,
              value: e,
            }))}
          />
        </Form.Item>

        <Form.Item label="Location" name="location">
          <Input placeholder="Zoom" />
        </Form.Item>

        <Form.Item label="Notes" name="body">
          <Input.TextArea rows={4} placeholder="Agenda, prep, etc." />
        </Form.Item>

        <Form.Item
          name="shouldFollowUp"
          wrapperCol={{ offset: 6, span: 18 }}
          valuePropName="checked"
        >
          <Checkbox size="small">Create follow-up</Checkbox>
        </Form.Item>

        {!isEmpty(contactEmails) ? null : (
          <div style={{ marginLeft: 24, marginTop: -8, opacity: 0.7 }}>
            Tip: add contact emails to prefill attendees.
          </div>
        )}
      </Form>
    </Modal>
  );
};
